import { formatNumber } from '@/i18n/format';

/**
 * scale10  - sms_score, surveys `score` / `avarage_score`, analytics `average_score`
 * scale100 - insights `overall_satisfaction.{ai,agent,overall}`
 */
export const ScoreScales = ['scale10', 'scale100'] as const;
export type ScoreScale = (typeof ScoreScales)[number];

const DIVISOR: Record<ScoreScale, number> = {
  scale10: 2,
  scale100: 20,
};

/**
 * Returns the score on a 0-5 scale, or null for no rating. Zero is a rating.
 * Non-finite and negative inputs are "not collected" sentinels, not zeros.
 */
export function scoreToFive(value: number | null, scale: ScoreScale): number | null {
  const divisor = DIVISOR[scale];
  if (!divisor) return null;
  if (value === null || value === undefined) return null;
  if (!Number.isFinite(value) || value < 0) return null;

  return Math.min(5, value / divisor);
}

/**
 * A score change on the 0-5 scale. Shares `scoreToFive`'s divisor so the two cannot
 * drift; a delta is signed and unbounded, so neither the 5 ceiling nor the
 * negative-as-sentinel rule applies.
 */
export function scoreDeltaToFive(delta: number, scale: ScoreScale): number {
  return delta / DIVISOR[scale];
}

/**
 * Renders a 0-5 score for display, always to one decimal and with the decimal mark of the UI
 * language ("4,5", as the mockup writes it). Null is no rating; zero is a rating.
 */
export function formatScoreOutOfFive(score: number | null): string {
  if (score === null) return '—';

  return formatNumber(score, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * The 0-5 average for a window, or null when nothing was rated. The count is the gate,
 * not the figure: an empty window returns 0, not null.
 */
export function averageRating(
  satisfaction?: { overall: number | null; responses: number | null } | null
): number | null {
  if (!satisfaction?.responses) return null;

  return scoreToFive(satisfaction.overall, 'scale100');
}
